<script lang="ts" setup>
import { computed, onMounted, onUnmounted, ref } from "vue";
import { useRouter } from "vue-router";
import { useApiClient } from "@/application/api";
import { useWebSocketClient } from "@/application/realtime";
import { useStore } from "@/application/store";
import LogoutModal from "./components/LogoutModal.vue";
import MainHeader from "./components/MainHeader.vue";
import MainSidebar from "./components/MainSidebar.vue";

const router = useRouter();
const store = useStore();
const apiClient = useApiClient();
const webSocketClient = useWebSocketClient();

const isLogoutModalOpen = ref(false);
const userName = computed(
  () =>
    store.state.authorization.user?.fullName ||
    store.state.authorization.user?.login ||
    ""
);
const canViewSystem = computed(
  () => hasAnyPermission(["system.read"]) || hasRole("superadministrator")
);
const canViewOperatingRooms = computed(
  () =>
    hasAnyPermission(["operating_rooms.read"]) || hasRole("superadministrator")
);
const canViewAdverseEvents = computed(
  () =>
    hasAnyPermission([
      "adverse_events.read",
      "adverse_event_dictionaries.manage",
    ]) || hasRole("superadministrator")
);
const canManageDepartments = computed(
  () =>
    hasAnyPermission(["departments.manage"]) || hasRole("superadministrator")
);
const canManageNotificationSettings = computed(
  () =>
    hasAnyPermission(["notification_settings.manage"]) ||
    hasRole("superadministrator")
);
const canViewUsers = computed(
  () =>
    hasAnyPermission(["users.manage", "roles.manage"]) ||
    hasRole("superadministrator")
);
const unreadNotifications = computed(
  () => store.state.notifications.unreadCount
);
let unsubscribeNotifications: (() => void) | null = null;

onMounted(() => {
  webSocketClient.connect();
  apiClient.notifications.list(1, 0).then((result) => {
    store.commit("notifications/setUnreadCount", result.unreadCount);
  }).catch(() => undefined);
  unsubscribeNotifications =
    webSocketClient.on<iSharedNotifications.NotificationDto>(
      "notification:created",
      (notification) => {
        store.commit("notifications/addNotification", notification);
      }
    );
});

onUnmounted(() => {
  webSocketClient.disconnect();
  unsubscribeNotifications?.();
});

function openLogoutModal(): void {
  isLogoutModalOpen.value = true;
}

function logout(): void {
  isLogoutModalOpen.value = false;
  apiClient.authorization.logout().then(() => {
    webSocketClient.disconnect();
    store.commit("authorization/clearUser");
    store.commit("notifications/clear");
    router.replace({ name: "login" });
  });
}

function hasPermission(
  permissionKey: iSharedPermission.PermissionKey
): boolean {
  return Boolean(
    store.state.authorization.user?.permissions.some(
      (permission) => permission.key === permissionKey
    )
  );
}

function hasAnyPermission(
  permissionKeys: iSharedPermission.PermissionKey[]
): boolean {
  return permissionKeys.some((permissionKey) => hasPermission(permissionKey));
}

function hasRole(roleName: iSharedUserRole.UserRoleName): boolean {
  return Boolean(
    store.state.authorization.user?.roles.some((role) => role.name === roleName)
  );
}
</script>

<template>
  <div
    class="grid h-screen grid-rows-[auto_minmax(0,1fr)] overflow-hidden bg-slate-100 text-slate-950 dark:bg-slate-950 dark:text-slate-50 md:grid-cols-[240px_minmax(0,1fr)] md:grid-rows-1"
  >
    <MainSidebar
      :can-view-system="canViewSystem"
      :can-view-operating-rooms="canViewOperatingRooms"
      :can-view-adverse-events="canViewAdverseEvents"
      :can-manage-departments="canManageDepartments"
      :can-manage-notification-settings="canManageNotificationSettings"
      :can-view-users="canViewUsers"
    />

    <section class="grid min-h-0 min-w-0 grid-rows-[3.5rem_minmax(0,1fr)]">
      <MainHeader
        :user-name="userName"
        :unread-notifications="unreadNotifications"
        @logout="openLogoutModal"
      />

      <main class="min-h-0 min-w-0 overflow-y-auto">
        <router-view></router-view>
      </main>
    </section>

    <LogoutModal v-model="isLogoutModalOpen" @confirm="logout" />
  </div>
</template>
